import { Components as JoyComponents, Theme as JoyTheme } from '@mui/joy';
import JoySwitch from '@mui/joy/Switch';
import { mergeProps, mergeRefs, useObjectRef } from '@react-aria/utils';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { Description, Label } from './fieldset';
import {
  ComposedFormControllerProps,
  FormController,
  useFormController,
} from './form';
import { createSplitProps } from './utils';

export const overrides: JoyComponents<JoyTheme>['JoySwitch'] = {
  defaultProps: {
    size: 'md',
  },
  styleOverrides: {
    thumb: () => ({
      transition: 'all 0.1s ease-in',
    }),
  },
};

export function SwitchGroup({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      data-slot='control'
      {...props}
      className={clsx(
        className,

        // Basic groups
        'space-y-6 [&_[data-slot=label]]:font-normal',

        // With descriptions
        'has-[[data-slot=description]]:space-y-7 [&_[data-slot=label]]:has-[[data-slot=description]]:font-medium',
      )}
    />
  );
}

export const Switch = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof JoySwitch> & { controlled?: boolean }
>(function Switch({ controlled = true, ...props }, ref) {
  const controller = useFormController();
  const objectRef = useObjectRef(ref);
  const mergedRef = mergeRefs(ref, objectRef);

  // When controlled, don't use defaultValue - let the controlled value handle it
  if (controller && controlled) {
    return (
      <JoySwitch
        data-slot='control'
        {...mergeProps(props, {
          ...controller.field,
          checked: controller.field.value ?? false,
        })}
      />
    );
  }

  // For uncontrolled switches, use defaultValue
  return (
    <JoySwitch
      data-slot='control'
      {...mergeProps(props, {
        defaultChecked: props.defaultChecked ?? false,
        ref: mergedRef,
      })}
    />
  );
});

export const switchFieldClasses = [
  // Base layout
  'grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1 sm:grid-cols-[auto_1fr]',

  // Control layout
  '[&>[data-slot=control]]:col-start-1 [&>[data-slot=control]]:self-center',

  // Label layout
  '[&>[data-slot=label]]:col-start-2 [&>[data-slot=label]]:row-start-1 [&>[data-slot=label]]:justify-self-start',

  // Description layout
  '[&>[data-slot=description]]:col-start-2 [&>[data-slot=description]]:row-start-2',

  // With description
  '[&_[data-slot=label]]:has-[[data-slot=description]]:font-medium',
];
export function SwitchField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  label,
  description,
  ...props
}: ComposedFormControllerProps<T, TName> &
  Omit<
    React.ComponentPropsWithoutRef<typeof Switch>,
    'ref' | keyof ComposedFormControllerProps<T, TName>
  > & {
    label?: React.ReactNode;
    description?: React.ReactNode;
  }) {
  const [controllerProps, controlProps] = createSplitProps<
    ComposedFormControllerProps<T, TName>
  >()(props, [
    'color',
    'color',
    'defaultValue',
    'disabled',
    'field',
    'control',
    'size',
    'required',
  ]);

  return (
    <FormController {...controllerProps}>
      <div className={clsx(switchFieldClasses)}>
        <Switch {...controlProps} />
        {label && <Label required={props.required}>{label}</Label>}
        {description && <Description>{description}</Description>}
      </div>
    </FormController>
  );
}
